import React, {
    useState,
    useMemo,
    useRef,
    useEffect
} from "react";
import {
    Form,
    Button,
    Table,
    Alert,
    Spinner,
    Row,
    Col
} from "react-bootstrap";
import API from "../../services/api";
import ThermalInvoicePrinter from "./ThermalInvoicePrinter";
import printThermalInvoice from "./ThermalInvoice";

const TableItemPayment = ({ tableData, onSuccess }) => {
    /* ===========================
       State
    ============================ */
    const [selectedItems, setSelectedItems] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [cardNumber, setCardNumber] = useState("");
    const [discount, setDiscount] = useState(0);

    const [cashReceived, setCashReceived] = useState("");
    const [customerExchange, setCustomerExchange] = useState("");

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [paymentResponse, setPaymentResponse] = useState(null);

    const printerRef = useRef();

    /* ===========================
       Helpers
    ============================ */

    const getItemKey = (orderId, itemId) => `${orderId}-${itemId}`;

    const addItem = (order, item) => {
        setSelectedItems((prev) => [
            ...prev,
            {
                key: getItemKey(order.id, item.id),
                order_id: order.id,
                item_id: item.id,
                unit_price: Number(item.total_price) / item.quantity,
                selected_qty: 1,
                max_qty: item.quantity,
            },
        ]);
    };

    const removeItem = (key) => {
        setSelectedItems((prev) => prev.filter((i) => i.key !== key));
    };

    const handlePrint = () => {
        const printWindow = window.open("", "_blank", "width=400,height=600");

        if (!printWindow) {
            alert("⚠️ Pop-up blocked! Please enable pop-ups for this site.");
            return;
        }

        printThermalInvoice(paymentResponse, printWindow);
    };

    const updateQuantity = (key, value) => {
        const qty = Math.max(1, Number(value) || 1);
        setSelectedItems((prev) =>
            prev.map((i) =>
                i.key === key
                    ? { ...i, selected_qty: Math.min(qty, i.max_qty) }
                    : i
            )
        );
    };

    /* ===========================
       Calculations
    ============================ */

    const selectedTotal = useMemo(() => {
        return selectedItems.reduce(
            (sum, i) => sum + i.unit_price * i.selected_qty,
            0
        );
    }, [selectedItems]);

    const payableTotal = useMemo(() => {
        return Math.max(selectedTotal - Number(discount || 0), 0);
    }, [selectedTotal, discount]);

    const updateLocalStorage = () => {
        const paymentData = {
            total: payableTotal.toFixed(0),
            discount,
            cashReceived,
            customerExchange,
        };

        localStorage.setItem("paymentData", JSON.stringify(paymentData));
    };

// Trigger save on every relevant change
    useEffect(() => {
        updateLocalStorage();
    }, [discount, cashReceived, customerExchange, payableTotal]);

    /* ===========================
       Cash change auto-calc
    ============================ */

    useEffect(() => {
        if (paymentMethod !== "cash") {
            setCashReceived("");
            setCustomerExchange("");
            return;
        }

        if (!cashReceived) {
            setCustomerExchange("");
            return;
        }

        const change = Number(cashReceived) - payableTotal;
        setCustomerExchange(change > 0 ? change.toFixed(0) : 0);
    }, [cashReceived, payableTotal, paymentMethod]);

    /* ===========================
       Submit
    ============================ */

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedItems.length) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const res = await API.put("/table-item-pay", {
                table_number: tableData.table_number,
                items: selectedItems.map((i) => ({
                    order_id: i.order_id,
                    order_item_id: i.item_id,
                    quantity: i.selected_qty,
                    price: i.unit_price
                })),
                payment_method: paymentMethod,
                card_number: paymentMethod === "card" ? cardNumber : null,
                discount: Number(discount || 0),
                total: payableTotal,
                customer_exchange: customerExchange,
                cash_received:cashReceived
            });

            setSuccess("✅ Payment completed successfully");
            setPaymentResponse(res.data); //

            setTimeout(() => {printerRef.current?.printInvoice();}, 100);

            setSelectedItems([]);
            setDiscount(0);
            setCardNumber("");
            setPaymentMethod("cash");
            setCashReceived("");
            setCustomerExchange("");

            // onSuccess?.();
        } catch (err) {
            console.error(err);
            setError("❌ Unable to process payment");
        } finally {
            setSaving(false);
        }
    };

    /* ===========================
       UI
    ============================ */

    const isCashInvalid =
        paymentMethod === "cash" &&
        Number(cashReceived || 0) < payableTotal;




    return (
        <div className="border rounded p-3 bg-light">
            <h5 className="mb-3">🧾 Item Payment</h5>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
                <>

                </>
            )}

            <Table bordered hover size="sm">
                <thead>
                <tr>
                    <th />
                    <th>Order</th>
                    <th>Item</th>
                    <th>Available</th>
                    <th>Pay Qty</th>
                    <th>Amount</th>
                </tr>
                </thead>
                <tbody>
                {tableData.orders.flatMap((order) =>
                    order.items.map((item) => {
                        const key = getItemKey(order.id, item.id);
                        const selected = selectedItems.find((i) => i.key === key);
                        const isPaid = item.status === "paid";

                        return (
                            <tr key={key} className={isPaid ? "opacity-50" : ""}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        disabled={isPaid}
                                        checked={!!selected}
                                        onChange={(e) =>
                                            e.target.checked
                                                ? addItem(order, item)
                                                : removeItem(key)
                                        }
                                    />
                                </td>
                                <td>#{order.id}</td>
                                <td>{item.name}</td>
                                <td>{item.quantity}</td>

                                <td style={{ width: 110 }}>
                                    {selected && (
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            min={1}
                                            max={item.quantity}
                                            value={selected.selected_qty}
                                            onChange={(e) =>
                                                updateQuantity(key, e.target.value)
                                            }
                                        />
                                    )}
                                </td>

                                <td>
                                    ¥
                                    {selected
                                        ? (
                                            selected.unit_price *
                                            selected.selected_qty
                                        ).toFixed(0)
                                        : Number(item.total_price).toFixed(0)}
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </Table>

            <Form onSubmit={handleSubmit}>
                <Row className="mb-2">
                    <Col>
                        <strong>Subtotal:</strong> ¥{selectedTotal.toFixed(0)}
                    </Col>
                </Row>

                <Form.Group className="mb-2">
                    <Form.Label>Discount</Form.Label>
                    <Form.Control
                        type="number"
                        min="0"
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                    />
                </Form.Group>

                <h6 className="text-danger">
                    Total Payable: ¥{payableTotal.toFixed(0)}
                </h6>

                <Form.Group className="mb-2">
                    <Form.Label>Payment Method</Form.Label>
                    <Form.Select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="qr">QR</option>
                    </Form.Select>
                </Form.Group>

                {paymentMethod === "cash" && (
                    <>
                        <Form.Group className="mb-2">
                            <Form.Label style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                                Cash Received
                            </Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={cashReceived}
                                placeholder="Enter cash received"
                                onChange={(e) => setCashReceived(e.target.value)}
                                style={{
                                    fontSize: "1.2rem",
                                    fontWeight: "bold",
                                    padding: "10px"
                                }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                                Customer Exchange (Change Returned)
                            </Form.Label>
                            <Form.Control
                                type="number"
                                readOnly
                                value={customerExchange}
                                placeholder="Auto calculated"
                                style={{
                                    fontSize: "1.2rem",
                                    fontWeight: "bold",
                                    padding: "10px",
                                    backgroundColor: "#f7f7f7"
                                }}
                            />
                        </Form.Group>
                    </>
                )}


                {paymentMethod === "card" && (
                    <Form.Group className="mb-3">
                        <Form.Label>Card Number</Form.Label>
                        <Form.Control
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                        />
                    </Form.Group>
                )}

                <Button
                    type="submit"
                    variant="success"
                    disabled={
                        saving ||
                        !selectedItems.length ||
                        isCashInvalid
                    }
                    className="w-100"
                >
                    {saving ? <Spinner size="sm" /> : "Confirm Payment"}
                </Button>
            </Form>

            {paymentResponse && (
                <div className=" mt-3">
                    <Button
                        variant="outline-primary"
                        onClick={handlePrint}
                    >
                        🖨️ Print Invoice
                    </Button>
                </div>
            )}
        </div>
    );
};

export default TableItemPayment;
